import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver } from "../../actions/valueActions";

import { convertBoolDiffValue } from "./valueFunctions";
import RiskValueDriverRevenueForm from "./RiskValueDriverRevenueForm";
import { getIconFromDriverName } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const RiskValueDriverRevenue = ({ id, valueModel, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [deleteRevenueDriverPopupOpen, setDeleteRevenueDriverPopupOpen] = useState(false);
    const [deleteRevenueDriverId, setDeleteRevenueDriverId] = useState(null);
    const [editRevenueDriverId, setEditRevenueDriverId] = useState(null);

    const openDeleteRevenueDriver = (id) => {
        setDeleteRevenueDriverId(id)
        setDeleteRevenueDriverPopupOpen(true)
    }

    const editRevenueDriver = (id) => {
        setEditRevenueDriverId(id)
    }

    const onSubmitRevenue = async (values, driver, { setSubmitting, setErrors }) => {
        // Clone the values object to avoid direct mutation
        const clonedValues = { ...values };
        clonedValues.id = editRevenueDriverId;
        if (outcome) {
            clonedValues.outcome = outcome;
        }
        clonedValues.annual_contract_value = clonedValues.annual_contract_value.toString().replace(/,/g, "");
        clonedValues.transaction_value = clonedValues.transaction_value.toString().replace(/,/g, "");

        let success = false

        if (editRevenueDriverId) {
            const result = await dispatch(editDriver('loss', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditRevenueDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('loss', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const handleResetRevenue = () => {
        setEditRevenueDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const revenueDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        { id: 'risk_reduction', label: 'Risk', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createRevenueDriverData = (id, outcome, driver_name, risk_reduction, positive_driver, actions) => {
        risk_reduction = `${risk_reduction} % ${positive_driver ? 'reduction' : 'increase'}`
        return { id, outcome, driver_name, risk_reduction, positive_driver, actions };
    }

    const revenueDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editRevenueDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteRevenueDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const revenueDriverRows = valueModel.risk_value_driver?.revenue_loss.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createRevenueDriverData(driver.id, driver.outcome, driver.driver_name, driver.risk_reduction, driver.positive_driver, revenueDriverActions(driver.id))
        )
    })

    const revenueDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={revenueDriverColumns}
                    rows={revenueDriverRows}
                    selectedId={editRevenueDriverId}
                />
            </React.Fragment>
        )
    }

    const revenueDriverFormActions = () => {
        return (
            <React.Fragment>
                {editRevenueDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editRevenueDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetRevenue}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Driver type:
                </Typography>
                <Typography variant="caption">
                    Revenue loss Risk Reduction
                </Typography>
            </Stack>


            <RiskValueDriverRevenueForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                editRevenueDriverId={editRevenueDriverId}
                editRevenueDriver={valueModel.risk_value_driver?.revenue_loss.find(revenueDriver => revenueDriver.id === editRevenueDriverId)}
                formActions={revenueDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitRevenue(values, driver, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {revenueDriverTable()}
            <DeletePopup open={deleteRevenueDriverPopupOpen} onClose={() => setDeleteRevenueDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('loss', id, deleteRevenueDriverId))} />
        </Box>
    )
}

export default RiskValueDriverRevenue